import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, AwardIcon, StarIcon, QuoteIcon } from 'lucide-react';
import { Coach } from './CoachCard';
interface CoachModalProps {
  coach: Coach | null;
  isOpen: boolean;
  onClose: () => void;
}
export function CoachModal({
  coach,
  isOpen,
  onClose
}: CoachModalProps) {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
  if (!coach) return null;
  const initials = coach.name.split(' ').map(n => n[0]).join('');
  return <AnimatePresence data-id="element-50">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-navy-900/60 backdrop-blur-sm z-50" data-id="element-51" />

          {/* Modal Container */}
          <div data-component="COMP-07-CoachModal" className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 pointer-events-none" role="dialog" aria-modal="true" aria-labelledby="coach-modal-title" data-id="element-52">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="bg-white w-full max-w-4xl max-h-[90vh] rounded-2xl shadow-lg overflow-hidden flex flex-col md:flex-row pointer-events-auto relative" data-id="element-53">
              {/* Close Button */}
              <button onClick={onClose} className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/80 backdrop-blur rounded-full flex items-center justify-center text-navy-900 hover:bg-navy-100 transition-colors" aria-label="Close modal" data-id="element-54">
                <XIcon className="w-5 h-5" data-id="element-55" />
              </button>

              {/* Left Column - Image & Quick Stats */}
              <div className="w-full md:w-2/5 bg-navy-50 p-8 flex flex-col items-center text-center border-r border-navy-100 overflow-y-auto" data-id="element-56">
                {coach.image ? <img src={coach.image} alt={coach.name} className="w-40 h-40 rounded-full object-cover border-4 border-white shadow-sm mb-6 mt-4" data-id="element-57" /> : <div className="w-40 h-40 rounded-full bg-navy-200 border-4 border-white shadow-sm flex items-center justify-center mb-6 mt-4" data-id="element-58">
                    <span className="text-5xl font-display font-bold text-navy-500" data-id="element-59">
                      {initials}
                    </span>
                  </div>}

                <h2 id="coach-modal-title" className="text-3xl font-bold text-navy-900 mb-2" data-id="element-60">
                  {coach.name}
                </h2>
                <p className="text-brand-blue font-bold text-lg mb-2" data-id="element-61">
                  {coach.credentials}
                </p>
                <div className="inline-block px-4 py-1.5 bg-navy-900 text-white text-sm font-bold uppercase tracking-wider rounded-full mb-8" data-id="element-62">
                  {coach.role}
                </div>

                <div className="w-full text-left space-y-4" data-id="element-63">
                  <h4 className="font-bold text-navy-900 uppercase tracking-wider text-sm flex items-center" data-id="element-64">
                    <AwardIcon className="w-4 h-4 mr-2 text-gold-500" data-id="element-65" />
                    Key Achievements
                  </h4>
                  <ul className="space-y-2" data-id="element-66">
                    {coach.achievements.map((achievement, i) => <li key={i} className="flex items-start text-sm text-navy-700" data-id="element-67">
                        <StarIcon className="w-4 h-4 mr-2 text-gold-400 flex-shrink-0 mt-0.5" data-id="element-68" />
                        <span data-id="element-69">{achievement}</span>
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Right Column - Bio & Philosophy */}
              <div className="w-full md:w-3/5 p-8 md:p-10 overflow-y-auto" data-id="element-70">
                <div className="mb-8" data-id="element-71">
                  <h3 className="text-2xl font-bold text-navy-900 mb-4" data-id="element-72">
                    About {coach.name.split(' ')[0]}
                  </h3>
                  <div className="prose prose-navy max-w-none text-navy-600 space-y-4" data-id="element-73">
                    {coach.fullBio.split('\n\n').map((paragraph, i) => <p key={i} data-id="element-74">{paragraph}</p>)}
                  </div>
                </div>

                <div className="bg-gold-50 rounded-2xl p-6 relative" data-id="element-75">
                  <QuoteIcon className="absolute top-4 left-4 w-8 h-8 text-gold-200" data-id="element-76" />
                  <div className="relative z-10 pl-6" data-id="element-77">
                    <h4 className="font-bold text-navy-900 mb-2" data-id="element-78">
                      Coaching Philosophy
                    </h4>
                    <p className="text-navy-700 italic" data-id="element-79">"{coach.philosophy}"</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}